import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { STATUS_COLOURS, Field, StatusChip } from "./_shared";

export function IncidentDetailSheet({ incident, onClose }) {
  return (
    <Sheet open={!!incident} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[600px] max-w-[92vw] overflow-y-auto">
        {incident && (
          <>
            <SheetHeader>
              <SheetTitle className="font-display text-xl text-slate-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                {incident.ref_number}
              </SheetTitle>
              <SheetDescription>
                {incident.incident_category} · {incident.classification}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date" value={incident.date_of_incident} />
                <Field label="BU" value={incident.business_unit_code} mono />
                <Field label="Location" value={incident.incident_location} full />
                <Field label="Employee" value={incident.employee_name} />
                <Field label="Reported by" value={incident.who_reported} />
                <Field label="Injured?" value={incident.was_injured ? "Yes" : "No"} />
                <Field label="Nature of injury" value={incident.nature_of_injury} />
                <Field label="Body part" value={incident.body_part_injured} />
                <Field label="Cause" value={incident.cause_of_injury} full />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Description</div>
                <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{incident.description}</div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <StatusChip label="SafeWork" active={incident.safework_notified} />
                <StatusChip label="Insurance" active={incident.insurance_notified} />
                <StatusChip label="Actions logged" active={incident.action_items_logged} />
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Source links</div>
                <div className="space-y-1.5 text-sm">
                  {incident.sharepoint_url && (
                    <a href={incident.sharepoint_url} target="_blank" rel="noopener noreferrer" className="text-gbe hover:underline flex items-center gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5" /> Open in SharePoint Incident Register
                    </a>
                  )}
                  {incident.aroflo_task_id && (
                    <a href={`https://app.aroflo.com/tasks/${incident.aroflo_task_id}`} target="_blank" rel="noopener noreferrer" className="text-gbe hover:underline flex items-center gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5" /> Aroflo Task #{incident.aroflo_task_id}
                    </a>
                  )}
                </div>
              </div>
              {incident.corrective_actions?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Corrective actions</div>
                  <div className="space-y-2">
                    {incident.corrective_actions.map((a) => (
                      <div key={a.id} className="rounded-md border border-slate-200 bg-white p-2.5 text-sm">
                        <div className="font-medium text-slate-900">{a.title}</div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-slate-500">{a.assigned_to || "Unassigned"}</span>
                          <Badge className={`text-[10px] ${STATUS_COLOURS[a.status]}`}>{a.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
